import { useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useHeroBanners } from '@/hooks/useHeroBanners';
import {
  heroBannerToItem,
  getBannerScheduleState,
  type HeroBanner,
  type HeroBannerInput,
  type HeroBannerItem,
} from '@/lib/heroBanners';
import { uploadMediaFile } from '@/hooks/useUpload';
import HeroBannerSlider from '@/components/feature/HeroBannerSlider';
import { getCommerceId } from '@/lib/ownership';
import BannerList from './components/BannerList';
import BannerEditorPreview from './components/BannerEditorPreview';

const EMPTY_FORM: HeroBannerInput = {
  title: '',
  subtitle: '',
  description: '',
  ctaLabel: '',
  ctaUrl: '',
  image: '',
  showText: true,
  textAlign: 'center',
  active: true,
  startDate: '',
  endDate: '',
};

/** Convertit une date ISO en valeur d'input datetime-local (heure locale). */
function toLocalInput(iso: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function BannersPage() {
  const { user } = useAuth();
  const merchantId = getCommerceId(user);
  const { banners, loading, error, create, update, remove, reorder } = useHeroBanners(merchantId);

  const [editing, setEditing] = useState<HeroBanner | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<HeroBannerInput>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [formError, setFormError] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setFormError('');
    setShowForm(true);
  };

  const openEdit = (banner: HeroBanner) => {
    setEditing(banner);
    setForm({
      title: banner.title,
      subtitle: banner.subtitle,
      description: banner.description,
      ctaLabel: banner.ctaLabel,
      ctaUrl: banner.ctaUrl,
      image: banner.image,
      showText: banner.showText,
      textAlign: banner.textAlign,
      active: banner.active,
      startDate: toLocalInput(banner.startDate),
      endDate: toLocalInput(banner.endDate),
    });
    setFormError('');
    setShowForm(true);
  };

  const handleUpload = async (file: File) => {
    setUploading(true);
    setFormError('');
    try {
      const url = await uploadMediaFile(file, 'banners');
      if (url) setForm((prev) => ({ ...prev, image: url }));
    } catch (err) {
      setFormError(err instanceof Error ? err.message : "Erreur lors de l'envoi de l'image");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    setFormError('');
    if (!form.image.trim() && !form.title.trim()) {
      setFormError('Ajoute au moins une image ou un titre à la bannière.');
      return;
    }
    if (form.startDate && form.endDate) {
      const s = new Date(form.startDate).getTime();
      const e = new Date(form.endDate).getTime();
      if (!Number.isNaN(s) && !Number.isNaN(e) && e < s) {
        setFormError('La date de fin doit être postérieure à la date de début.');
        return;
      }
    }
    setSaving(true);
    try {
      if (editing) {
        await update(editing.id, form);
      } else {
        await create(form);
      }
      setShowForm(false);
    } catch (err) {
      setFormError(err instanceof Error ? err.message : 'Erreur lors de l\u2019enregistrement.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (banner: HeroBanner) => {
    if (!window.confirm('Supprimer cette bannière ?')) return;
    try {
      await remove(banner.id);
    } catch {
      /* silencieux */
    }
  };

  const previewItems = useMemo(
    () => banners.filter((b) => b.active && (b.image.trim() || b.title.trim())).map(heroBannerToItem),
    [banners],
  );

  const formPreviewItem = useMemo<HeroBannerItem>(
    () => ({
      id: editing?.id ?? 0,
      image: form.image,
      title: form.title,
      subtitle: form.subtitle,
      description: form.description,
      ctaLabel: form.ctaLabel,
      ctaUrl: form.ctaUrl,
      showText: form.showText,
      textAlign: form.textAlign,
    }),
    [editing, form],
  );

  const formScheduleState = getBannerScheduleState({
    startDate: form.startDate,
    endDate: form.endDate,
  });

  return (
    <div className="p-4 md:p-6">
      <Link
        to="/dashboard"
        className="inline-flex items-center gap-1.5 mb-4 text-sm font-medium text-foreground-500 hover:text-foreground-900 transition-colors no-underline cursor-pointer"
      >
        <i className="ri-arrow-left-line"></i>
        Retour au tableau de bord
      </Link>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">Bannières d&apos;accueil</h2>
          <p className="text-sm text-foreground-500 mt-1">
            Ajoutez une ou plusieurs bannières (image, titre, paragraphe). Plusieurs bannières = diaporama automatique.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={() => window.dispatchEvent(new Event('zifek-toggle-sidebar'))}
            className="flex items-center gap-2 px-4 py-2 rounded-full border border-background-200/70 bg-background-50 text-foreground-700 text-sm font-medium whitespace-nowrap hover:bg-background-100 transition-colors cursor-pointer print:hidden"
            title="Replier ou déplier le menu pour élargir l’espace"
          >
            <i className="ri-menu-fold-line"></i>
            <span className="hidden sm:inline">Menu</span>
          </button>
          <button
            onClick={openCreate}
            className="flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            <i className="ri-add-line"></i>
            Nouvelle bannière
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_420px] gap-6 items-start">
        <div>
          {error && (
            <div className="mb-4 px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
              <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
              <span className="text-xs text-red-700">{error}</span>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
            </div>
          ) : banners.length === 0 ? (
            <div className="bg-background-50 border border-dashed border-background-300/60 rounded-lg p-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-background-100 flex items-center justify-center mx-auto mb-3">
                <i className="ri-gallery-view-2 text-2xl text-foreground-400"></i>
              </div>
              <p className="text-sm font-medium text-foreground-700">Aucune bannière pour l&apos;instant</p>
              <p className="text-xs text-foreground-500 mt-1 mb-4">
                Créez votre première bannière : elle s&apos;affichera en haut de votre page d&apos;accueil.
              </p>
              <button
                onClick={openCreate}
                className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
              >
                <i className="ri-add-line"></i>
                Créer une bannière
              </button>
            </div>
          ) : (
            <>
              {banners.length > 1 && (
                <p className="text-xs text-foreground-400 mb-2 flex items-center gap-1.5">
                  <i className="ri-draggable"></i>
                  Glissez-déposez les bannières pour changer leur ordre d&apos;affichage.
                </p>
              )}
              <BannerList
                banners={banners}
                onReorder={reorder}
                onEdit={openEdit}
                onDelete={handleDelete}
              />
            </>
          )}
        </div>

        {/* Aperçu en direct */}
        <div className="lg:sticky lg:top-20">
          <div className="flex items-center gap-2 mb-3">
            <i className="ri-eye-line text-accent-500"></i>
            <h3 className="text-sm font-semibold text-foreground-700">Aperçu du diaporama</h3>
          </div>
          <div className="rounded-xl border border-background-200/70 overflow-hidden bg-background-50">
            {previewItems.length > 0 ? (
              <HeroBannerSlider banners={previewItems} className="h-[240px]" autoPlay />
            ) : (
              <div className="h-[240px] flex flex-col items-center justify-center text-center px-6">
                <i className="ri-gallery-view-2 text-2xl text-foreground-300"></i>
                <p className="text-xs text-foreground-400 mt-2">Aucune bannière active à prévisualiser.</p>
              </div>
            )}
          </div>
          <p className="text-xs text-foreground-400 mt-2">
            Ce diaporama s&apos;affiche en haut de votre page d&apos;accueil. Utilisez la balise <code className="bg-background-100 px-1 rounded">{'{{hero_banners}}'}</code> pour le placer où vous voulez dans un thème.
            Pour n&apos;afficher qu&apos;<strong className="text-foreground-600">une bannière précise</strong> sur une page, utilise son <em>id</em> : <code className="bg-background-100 px-1 rounded">{'{{hero_banners banner="ID"}}'}</code> (ou la 1re : <code className="bg-background-100 px-1 rounded">{'{{hero_banners limit="1"}}'}</code>).
          </p>
        </div>
      </div>

      {/* Formulaire (modale) */}
      {showForm && (
        <>
          <div className="fixed inset-0 bg-black/30 z-50" onClick={saving ? undefined : () => setShowForm(false)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-2xl bg-background-50 rounded-lg border border-background-200/70 flex flex-col" style={{ maxHeight: '92vh' }}>
            <div className="flex items-center justify-between px-5 h-14 border-b border-background-200/70 flex-shrink-0">
              <h3 className="text-base font-semibold text-foreground-950">
                {editing ? 'Modifier la bannière' : 'Nouvelle bannière'}
              </h3>
              <button
                onClick={() => setShowForm(false)}
                disabled={saving}
                className="w-7 h-7 flex items-center justify-center rounded-md hover:bg-background-100 transition-colors cursor-pointer disabled:opacity-40"
              >
                <i className="ri-close-line text-foreground-500"></i>
              </button>
            </div>

            <div className="overflow-y-auto p-5 flex-1 space-y-4">
              {/* Image */}
              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-2">Image de la bannière</label>
                <div className="flex items-center gap-3">
                  <div className="w-24 h-16 rounded-md overflow-hidden bg-background-100 border border-background-200/70 flex items-center justify-center flex-shrink-0">
                    {form.image ? (
                      <img src={form.image} alt="" className="w-full h-full object-cover" />
                    ) : (
                      <i className="ri-image-line text-xl text-foreground-300"></i>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={() => fileRef.current?.click()}
                      disabled={uploading}
                      className="flex items-center gap-2 px-3 py-2 bg-background-100 border border-background-200/70 rounded-lg text-xs font-medium text-foreground-700 hover:bg-background-200/70 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-50"
                    >
                      {uploading ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-upload-2-line"></i>}
                      {form.image ? 'Changer l\u2019image' : 'Ajouter une image'}
                    </button>
                    {form.image && (
                      <button
                        type="button"
                        onClick={() => setForm((p) => ({ ...p, image: '' }))}
                        className="text-xs text-foreground-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap text-left"
                      >
                        <i className="ri-delete-bin-line mr-1"></i>Retirer
                      </button>
                    )}
                  </div>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (file) handleUpload(file);
                      if (fileRef.current) fileRef.current.value = '';
                    }}
                  />
                </div>
                <p className="text-[11px] text-foreground-400 mt-1.5">
                  Tu peux laisser le titre vide : la bannière sera juste une image.
                </p>
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Titre</label>
                <input
                  type="text"
                  value={form.title}
                  onChange={(e) => setForm((p) => ({ ...p, title: e.target.value }))}
                  maxLength={90}
                  placeholder="Ex : Nouvelle collection d'été"
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Petit paragraphe</label>
                <textarea
                  value={form.subtitle}
                  onChange={(e) => setForm((p) => ({ ...p, subtitle: e.target.value }))}
                  rows={2}
                  maxLength={180}
                  placeholder="Ex : Jusqu'à -40% sur une sélection de produits."
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-foreground-600 mb-1.5">Petite description (optionnel)</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((p) => ({ ...p, description: e.target.value }))}
                  rows={2}
                  maxLength={220}
                  placeholder="Texte secondaire affiché sous le paragraphe."
                  className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors resize-none"
                />
              </div>

              {/* Affichage du texte sur l'image */}
              <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-3">
                <button
                  type="button"
                  onClick={() => setForm((p) => ({ ...p, showText: !p.showText }))}
                  className="w-full flex items-center justify-between gap-3 cursor-pointer"
                >
                  <span className="text-left">
                    <span className="block text-xs font-semibold text-foreground-700 flex items-center gap-1.5">
                      <i className={form.showText ? 'ri-text text-secondary-600' : 'ri-image-line text-foreground-400'}></i>
                      Afficher le titre et la description
                    </span>
                    <span className="block text-[11px] text-foreground-500 mt-0.5">
                      {form.showText
                        ? 'Le texte s’affiche par-dessus l’image, avec un voile pour rester lisible.'
                        : 'Seule l’image est affichée, en clair et sans voile sombre.'}
                    </span>
                  </span>
                  <span className={`flex-shrink-0 w-9 h-5 rounded-full transition-colors relative ${form.showText ? 'bg-primary-500' : 'bg-background-300/60'}`}>
                    <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-all ${form.showText ? 'left-[18px]' : 'left-0.5'}`}></span>
                  </span>
                </button>

                {form.showText && (
                  <div className="mt-3 pt-3 border-t border-background-200/70">
                    <label className="block text-xs font-medium text-foreground-600 mb-1.5">Position du texte</label>
                    <div className="grid grid-cols-3 gap-2">
                      {([
                        { key: 'left', label: 'Gauche', icon: 'ri-align-left' },
                        { key: 'center', label: 'Centre', icon: 'ri-align-center' },
                        { key: 'right', label: 'Droite', icon: 'ri-align-right' },
                      ] as const).map((opt) => (
                        <button
                          key={opt.key}
                          type="button"
                          onClick={() => setForm((p) => ({ ...p, textAlign: opt.key }))}
                          className={`flex flex-col items-center justify-center gap-1 px-2 py-2 rounded-lg border text-[11px] font-medium transition-colors cursor-pointer whitespace-nowrap ${
                            form.textAlign === opt.key
                              ? 'border-primary-300 bg-primary-50 text-primary-700'
                              : 'border-background-200/70 bg-background-50 text-foreground-600 hover:bg-background-100'
                          }`}
                        >
                          <i className={`${opt.icon} text-base`}></i>
                          {opt.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-[11px] text-foreground-400 mt-2">
                      L’alignement est respecté sur ordinateur comme sur mobile, avec une marge automatique pour que le texte reste lisible.
                    </p>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Texte du bouton</label>
                  <input
                    type="text"
                    value={form.ctaLabel}
                    onChange={(e) => setForm((p) => ({ ...p, ctaLabel: e.target.value }))}
                    maxLength={40}
                    placeholder="Ex : Découvrir"
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1.5">Lien du bouton</label>
                  <input
                    type="text"
                    value={form.ctaUrl}
                    onChange={(e) => setForm((p) => ({ ...p, ctaUrl: e.target.value }))}
                    placeholder="Ex : /products"
                    className="w-full px-3 py-2.5 bg-background-100 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                  />
                </div>
              </div>

              {/* Période de promo */}
              <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-3">
                <div className="flex items-center justify-between gap-2 mb-2">
                  <label className="text-xs font-medium text-foreground-600 flex items-center gap-1.5">
                    <i className="ri-calendar-event-line text-secondary-600"></i>
                    Période d&apos;activation (optionnel)
                  </label>
                  {formScheduleState === 'scheduled' && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-secondary-100 text-secondary-900 whitespace-nowrap">
                      <i className="ri-time-line"></i>Programmée
                    </span>
                  )}
                  {formScheduleState === 'live' && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-700 whitespace-nowrap">
                      <i className="ri-flashlight-fill"></i>En promo
                    </span>
                  )}
                  {formScheduleState === 'expired' && (
                    <span className="inline-flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded-full bg-background-200/70 text-foreground-500 whitespace-nowrap">
                      <i className="ri-history-line"></i>Expirée
                    </span>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <label className="block text-[11px] text-foreground-500 mb-1">Début</label>
                    <input
                      type="datetime-local"
                      value={form.startDate}
                      onChange={(e) => setForm((p) => ({ ...p, startDate: e.target.value }))}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] text-foreground-500 mb-1">Fin</label>
                    <input
                      type="datetime-local"
                      value={form.endDate}
                      onChange={(e) => setForm((p) => ({ ...p, endDate: e.target.value }))}
                      className="w-full px-3 py-2 bg-background-50 border border-background-200/70 rounded-lg text-sm text-foreground-900 focus:outline-none focus:border-primary-300 transition-colors"
                    />
                  </div>
                </div>
                <div className="flex items-center justify-between mt-2">
                  <p className="text-[11px] text-foreground-400">
                    La bannière s&apos;affiche automatiquement entre ces dates. Laisse vide pour toujours l&apos;afficher.
                  </p>
                  {(form.startDate || form.endDate) && (
                    <button
                      type="button"
                      onClick={() => setForm((p) => ({ ...p, startDate: '', endDate: '' }))}
                      className="text-[11px] text-foreground-400 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap ml-2"
                    >
                      <i className="ri-close-circle-line mr-1"></i>Effacer
                    </button>
                  )}
                </div>
              </div>

              <button
                type="button"
                onClick={() => setForm((p) => ({ ...p, active: !p.active }))}
                className="w-full flex items-center justify-between px-3 py-2.5 rounded-lg bg-background-100 border border-background-200/70 cursor-pointer"
              >
                <span className="text-sm text-foreground-700 flex items-center gap-2">
                  <i className={form.active ? 'ri-eye-line text-accent-600' : 'ri-eye-off-line text-foreground-400'}></i>
                  Afficher cette bannière
                </span>
                <span className={`w-9 h-5 rounded-full transition-colors relative ${form.active ? 'bg-primary-500' : 'bg-background-300/60'}`}>
                  <span className={`absolute top-0.5 w-4 h-4 rounded-full bg-background-50 transition-all ${form.active ? 'left-[18px]' : 'left-0.5'}`}></span>
                </span>
              </button>

              {/* Aperçu multi-écrans */}
              <div className="pt-1 border-t border-background-200/70">
                <div className="pt-4">
                  <BannerEditorPreview item={formPreviewItem} />
                </div>
              </div>

              {formError && (
                <div className="px-3 py-2.5 rounded-md bg-red-50 border border-red-100 flex items-start gap-2">
                  <i className="ri-error-warning-line text-red-500 text-sm flex-shrink-0 mt-0.5"></i>
                  <span className="text-xs text-red-700">{formError}</span>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-5 h-14 border-t border-background-200/70 flex-shrink-0">
              <button
                onClick={() => setShowForm(false)}
                disabled={saving}
                className="px-4 py-2 rounded-full text-sm font-medium text-foreground-600 hover:text-foreground-800 transition-colors cursor-pointer whitespace-nowrap disabled:opacity-40"
              >
                Annuler
              </button>
              <button
                onClick={handleSave}
                disabled={saving || uploading}
                className="px-5 py-2 rounded-full text-sm font-semibold bg-primary-500 text-background-50 whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
                {editing ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}